import React, { useMemo, useState } from 'react';
import { Box, Typography, Tooltip, IconButton, TableSortLabel } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import { bloomberg } from '../theme/bloombergTheme';
import { TickerChip } from './TickerActionMenu';
import { LeagueHolding } from '../services/api';

/**
 * Holdings of a systematic theme model. Every name is the same weight, so the
 * table shows what differs instead: how each name is doing, its size, and
 * which of your other models also own it.
 * Read-only by design — a theme base changes by Rebuild, or through a
 * Active version, never by hand.
 */

type SortKey = 'symbol' | 'r1d' | 'r1m' | 'r3m' | 'r1y' | 'mcap' | 'also';
const MONO = bloomberg.font.mono;
const pct = (v?: number | null) => (v === null || v === undefined ? '—' : `${v >= 0 ? '+' : '−'}${Math.abs(v * 100).toFixed(1)}%`);
const heat = (v?: number | null): React.CSSProperties => {
  if (v === null || v === undefined) return { color: bloomberg.text.muted };
  const a = Math.min(0.5, Math.abs(v * 100) / 70 + 0.05);
  return v >= 0 ? { background: `rgba(34,197,94,${a.toFixed(2)})`, color: '#dcfce7' } : { background: `rgba(239,68,68,${a.toFixed(2)})`, color: '#fee2e2' };
};
const money = (v?: number | null) => (!v ? '—' : v >= 1e12 ? `$${(v / 1e12).toFixed(1)}tn` : `$${(v / 1e9).toFixed(v >= 1e10 ? 0 : 1)}bn`);

export default function ThemeHoldingsTable({ holdings, buildSpec, perf = {}, loading, onResearch }: {
  holdings: { symbol: string; name?: string | null }[];
  buildSpec?: Record<string, any> | null;
  perf?: Record<string, LeagueHolding>;
  loading: boolean;
  onResearch: (symbol: string) => void;
}) {
  const [sort, setSort] = useState<SortKey>('r3m');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const spec: Record<string, any> = useMemo(
    () => Object.fromEntries(((buildSpec?.holdings as any[]) || []).map((h) => [h.symbol, h])), [buildSpec]);

  const rows = useMemo(() => {
    const val = (h: { symbol: string }): number | string => {
      const p = perf[h.symbol] || ({} as LeagueHolding);
      const s = spec[h.symbol] || {};
      switch (sort) {
        case 'symbol': return h.symbol;
        case 'mcap': return s.mcap_usd || 0;
        case 'also': return (p.also_in || []).length;
        default: return (p[sort] as number | null | undefined) ?? -Infinity;
      }
    };
    return [...holdings].sort((a, b) => {
      const x = val(a), y = val(b);
      const c = typeof x === 'string' ? x.localeCompare(String(y)) : (x as number) - (y as number);
      return dir === 'asc' ? c : -c;
    });
  }, [holdings, perf, spec, sort, dir]);

  const head = (key: SortKey, label: string, align: 'left' | 'right' | 'center' = 'right', title?: string) => (
    <th scope="col" style={{ textAlign: align, padding: '8px 8px', color: bloomberg.text.muted, fontWeight: 600, fontSize: 12,
      borderBottom: `1px solid ${bloomberg.border.secondary}`, whiteSpace: 'nowrap' }} title={title}>
      <TableSortLabel active={sort === key} direction={sort === key ? dir : 'desc'}
        onClick={() => { if (sort === key) setDir(dir === 'asc' ? 'desc' : 'asc'); else { setSort(key); setDir(key === 'symbol' ? 'asc' : 'desc'); } }}>
        {label}
      </TableSortLabel>
    </th>
  );
  const td: React.CSSProperties = { padding: '5px 8px', borderBottom: `1px solid ${bloomberg.border.primary}`, textAlign: 'right', fontFamily: MONO, fontSize: 12.5, whiteSpace: 'nowrap' };
  const n = holdings.length || 1;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Holdings</Typography>
        <Typography sx={{ fontSize: 12.5, color: bloomberg.accent.orangeLight }}>
          {holdings.length} names · equal weight, {(100 / n).toFixed(1)}% each
        </Typography>
        <Typography sx={{ fontSize: 12, color: bloomberg.text.muted }}>
          Systematic: to change it, Rebuild from its ETFs or create an Active version.
        </Typography>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', color: bloomberg.text.primary }}>
          <thead>
            <tr>
              {head('symbol', 'Symbol', 'left')}
              <th scope="col" style={{ textAlign: 'left', padding: '8px', color: bloomberg.text.muted, fontWeight: 600, fontSize: 12, borderBottom: `1px solid ${bloomberg.border.secondary}` }}>Company</th>
              {head('r1d', '1D')}{head('r1m', '1M')}{head('r3m', '3M')}{head('r1y', '1Y')}
              <th scope="col" style={{ textAlign: 'center', padding: '8px', color: bloomberg.text.muted, fontWeight: 600, fontSize: 12, borderBottom: `1px solid ${bloomberg.border.secondary}`, whiteSpace: 'nowrap' }}
                title="Price above its 200-day average">200-day</th>
              {head('mcap', 'Mkt cap')}
              {head('also', 'Also in', 'left', 'Your other models that hold this stock')}
              <th scope="col" style={{ borderBottom: `1px solid ${bloomberg.border.secondary}`, width: 36 }}><span style={{ position: 'absolute', left: -9999 }}>Research</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((h) => {
              const p = perf[h.symbol] || ({} as LeagueHolding);
              const s = spec[h.symbol] || {};
              const also = p.also_in || [];
              return (
                <tr key={h.symbol}>
                  <td style={{ ...td, textAlign: 'left' }}><TickerChip symbol={h.symbol} /></td>
                  <td style={{ ...td, textAlign: 'left', fontFamily: 'inherit', fontSize: 13, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name || h.name || h.symbol}</td>
                  <td style={{ ...td, color: (p.r1d ?? 0) >= 0 ? '#22c55e' : '#f87171' }}>{loading ? '…' : pct(p.r1d)}</td>
                  <td style={{ ...td, ...heat(p.r1m) }}>{loading ? '…' : pct(p.r1m)}</td>
                  <td style={{ ...td, ...heat(p.r3m) }}>{loading ? '…' : pct(p.r3m)}</td>
                  <td style={{ ...td, ...heat(p.r1y) }}>{loading ? '…' : pct(p.r1y)}</td>
                  <td style={{ ...td, textAlign: 'center', fontFamily: 'inherit', color: p.above_200 === true ? '#22c55e' : p.above_200 === false ? '#f87171' : bloomberg.text.muted }}>
                    {p.above_200 === true ? 'above' : p.above_200 === false ? 'below' : '—'}
                  </td>
                  <td style={td}>{money(s.mcap_usd)}</td>
                  <td style={{ ...td, textAlign: 'left', fontFamily: 'inherit', fontSize: 12, color: also.length ? '#fcd34d' : bloomberg.text.muted, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {also.length ? (
                      <Tooltip title={also.join(', ')}><span style={{ cursor: 'help' }}>{also.length} model{also.length > 1 ? 's' : ''} · {also[0]}{also.length > 1 ? '…' : ''}</span></Tooltip>
                    ) : '—'}
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <IconButton size="small" onClick={() => onResearch(h.symbol)} sx={{ color: '#a855f7' }} aria-label={`Research ${h.symbol}`}>
                      <ArticleIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}
